import { z } from 'zod';
import { PRICED_CLEARANCE_TIERS, PRICED_VERSION_PACKS } from './script';

/**
 * What can be bought for a script project after its report was ordered: a pack of additional clearances or of
 * script versions. They are priced from the same matrix cells as the tiers a main order can choose up front, so
 * a pack costs the same whenever it is bought. No screen orders one yet.
 */
export const ADDON_PRODUCTS = ['script_additional_clearances', 'script_versions'] as const;
export const addonProductSchema = z.enum(ADDON_PRODUCTS);
export type AddonProduct = z.infer<typeof addonProductSchema>;

export const scriptAdditionalClearancesOptionsSchema = z.strictObject({
  quantity: z.literal(PRICED_CLEARANCE_TIERS),
});
export const scriptVersionsOptionsSchema = z.strictObject({
  quantity: z.literal(PRICED_VERSION_PACKS),
});

/** Options type per add-on product. */
export type AddonOptions = {
  script_additional_clearances: z.infer<typeof scriptAdditionalClearancesOptionsSchema>;
  script_versions: z.infer<typeof scriptVersionsOptionsSchema>;
};

const ADDON_OPTION_SCHEMAS = {
  script_additional_clearances: scriptAdditionalClearancesOptionsSchema,
  script_versions: scriptVersionsOptionsSchema,
} as const;

/** The options schema of an add-on product. Mirrors `order_options_valid` in SQL. */
export function addonOptionsSchema<P extends AddonProduct>(
  product: P,
): (typeof ADDON_OPTION_SCHEMAS)[P] {
  return ADDON_OPTION_SCHEMAS[product];
}

/**
 * What a script project is entitled to (`project_entitlements`): summed over its placed orders, the main order's
 * own choices plus every add-on pack. A cancelled or refunded order counts for nothing.
 */
export const projectEntitlementsSchema = z.object({
  additional_clearances: z.number().int().nonnegative(),
  versions: z.number().int().nonnegative(),
});
export type ProjectEntitlements = z.infer<typeof projectEntitlementsSchema>;
